import React, { useEffect } from 'react';
import { 
  Grid, 
  FormControlLabel, 
  Switch, 
  Paper, 
  Typography, 
  Box,
  Divider,
  Tooltip,
  IconButton
} from '@material-ui/core';
import { Security, HelpOutline } from '@material-ui/icons';

const CheatDetectionConfig = ({
  cheatDetectionSettings,
  setCheatDetectionSettings,
  validationErrors = {}
}) => {
  
  // Debug logging for cheat detection settings changes
  useEffect(() => {
    console.log('🔒 CheatDetectionConfig: Settings updated:', cheatDetectionSettings);
  }, [cheatDetectionSettings]);

  // Ensure we have valid settings object
  const safeSettings = cheatDetectionSettings || {};
  
  // Show loading state if settings are not loaded yet
  if (!cheatDetectionSettings || Object.keys(cheatDetectionSettings).length === 0) {
    return (
      <Paper elevation={2} style={{ padding: '24px', marginBottom: '24px' }}>
        <Box display="flex" alignItems="center" marginBottom="16px">
          <Security style={{ marginRight: '8px', color: '#1976d2' }} />
          <Typography variant="h6" style={{ fontWeight: 600, color: '#1976d2' }}>
            Security & Cheat Detection Settings
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Loading security settings...
        </Typography>
      </Paper>
    );
  }
  
  const handleSettingChange = (setting, value) => {
    console.log(`🔒 CheatDetectionConfig: Changing ${setting} to ${value}`);
    setCheatDetectionSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const resetToDefaults = () => {
    console.log('🔒 CheatDetectionConfig: Resetting to default settings');
    setCheatDetectionSettings({
      enableTabSwitchDetection: true,
      enableCopyPasteDetection: true,
      enableFullscreenDetection: true,
      enableWebcamDetection: true,
      enableKeyboardDetection: true,
      enableContextMenuDetection: true,
      enableSidePanelDetection: true,
      enableFocusLossDetection: true
    });
  };

  const cheatDetectionOptions = [
    {
      key: 'enableTabSwitchDetection',
      label: 'Tab Switch Detection',
      description: 'Detects when user switches browser tabs or windows',
      icon: '🔄'
    },
    {
      key: 'enableCopyPasteDetection',
      label: 'Copy-Paste Detection',
      description: 'Detects copy-paste operations during exam',
      icon: '📋'
    },
    {
      key: 'enableFullscreenDetection',
      label: 'Fullscreen Detection',
      description: 'Enforces fullscreen mode and detects exits',
      icon: '🖥️'
    },
    {
      key: 'enableWebcamDetection',
      label: 'Webcam Monitoring',
      description: 'Monitors webcam for person detection and suspicious objects',
      icon: '📹'
    },
    {
      key: 'enableKeyboardDetection',
      label: 'Keyboard Shortcut Detection',
      description: 'Detects Alt/Ctrl key usage and keyboard shortcuts',
      icon: '⌨️'
    },
    {
      key: 'enableContextMenuDetection',
      label: 'Right-Click Detection',
      description: 'Disables and detects right-click context menu usage',
      icon: '🖱️'
    },
    {
      key: 'enableSidePanelDetection',
      label: 'Side Panel Detection',
      description: 'Detects AI sidebars (Copilot), browser side panels, devtools and split screen',
      icon: '📐'
    },
    {
      key: 'enableFocusLossDetection',
      label: 'Window Focus Detection',
      description: 'Detects the candidate clicking outside the assessment window or onto another screen',
      icon: '🎯'
    }
  ];

  return (
    <Paper elevation={2} style={{ padding: '24px', marginBottom: '24px' }}>
      <Box display="flex" alignItems="center" marginBottom="16px">
        <Security style={{ marginRight: '8px', color: '#1976d2' }} />
        <Typography variant="h6" style={{ fontWeight: 600, color: '#1976d2' }}>
          Security & Cheat Detection Settings
        </Typography>
        <Tooltip title="Configure which security features to enable for this assessment">
          <IconButton size="small" style={{ marginLeft: '8px' }}>
            <HelpOutline fontSize="small" />
          </IconButton>
        </Tooltip>
        {safeSettings && Object.keys(safeSettings).length > 0 && (
          <Box 
            style={{ 
              marginLeft: '12px', 
              padding: '4px 8px', 
              backgroundColor: '#e8f5e8', 
              borderRadius: '4px',
              fontSize: '12px',
              color: '#2e7d32',
              fontWeight: 500
            }}
          >
            ✓ Settings Loaded
          </Box>
        )}
        <Box style={{ marginLeft: 'auto' }}>
          <button
            onClick={resetToDefaults}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666',
              cursor: 'pointer',
              fontWeight: 500
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e0e0e0';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
            }}
          >
            Reset to Defaults
          </button>
        </Box>
      </Box>
      
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '20px' }}>
        Select which security features should be active during this assessment. 
        All features are enabled by default for maximum security.
      </Typography>

      <Grid container spacing={3}>
        {cheatDetectionOptions.map((option, index) => (
          <Grid item xs={12} sm={6} md={4} key={option.key}>
            <Paper 
              elevation={1} 
              style={{ 
                padding: '16px', 
                border: safeSettings[option.key] 
                  ? '2px solid #4caf50' 
                  : '1px solid #e0e0e0',
                backgroundColor: safeSettings[option.key] 
                  ? '#f1f8e9' 
                  : '#fafafa',
                transition: 'all 0.2s ease'
              }}
            >
              <Box display="flex" alignItems="center" marginBottom="8px">
                <span style={{ fontSize: '20px', marginRight: '8px' }}>
                  {option.icon}
                </span>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {option.label}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                {option.description}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={!!safeSettings[option.key]}
                    onChange={(e) => handleSettingChange(option.key, e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {safeSettings[option.key] ? 'Enabled' : 'Disabled'}
                  </Typography>
                }
                style={{ margin: 0 }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Divider style={{ margin: '20px 0' }} />
      
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1976d2' }}>
            Security Level
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {Object.values(safeSettings).filter(Boolean).length} of {cheatDetectionOptions.length} features enabled
          </Typography>
        </Box>
        
        <Box display="flex" gap="8px">
          <Typography 
            variant="body2" 
            style={{ 
              color: Object.values(safeSettings).filter(Boolean).length >= 4 
                ? '#4caf50' 
                : Object.values(safeSettings).filter(Boolean).length >= 2 
                  ? '#ff9800' 
                  : '#f44336',
              fontWeight: 600
            }}
          >
            {Object.values(safeSettings).filter(Boolean).length >= 4 
              ? 'High Security' 
              : Object.values(safeSettings).filter(Boolean).length >= 2 
                ? 'Medium Security' 
                : 'Low Security'
            }
          </Typography>
        </Box>
      </Box>

      {validationErrors.cheatDetection && (
        <Typography variant="body2" color="error" style={{ marginTop: '8px' }}>
          {validationErrors.cheatDetection}
        </Typography>
      )}
    </Paper>
  );
};

export default CheatDetectionConfig;
